import { useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router";
import { useNavigationStore, type DesktopSectionId } from "@/stores/navigationStore";
import type { TabProps } from "./types";
import { scrollToElement } from "@/lib/scroll";

// Mapping from desktop href to tracked section IDs (only sections wired into navigationStore)
const DESKTOP_HREF_TO_SECTION_MAP: Record<string, DesktopSectionId> = {
  '#home': 'home',
  '#how-i-work': 'how-i-work',
  '#case-studies': 'case-studies',
  '#skills': 'skills',
  '#about-me': 'about-me',
};

export const Tab = ({ children, setPosition, href, isActive }: TabProps) => {
  const ref = useRef<HTMLLIElement>(null);
  const { setIsNavigating, setActiveSection } = useNavigationStore();
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    // Support both "#section" and "/path#section" formats.
    const hashIndex = href.indexOf('#');
    const path = hashIndex === -1 ? href : href.slice(0, hashIndex);
    const hash = hashIndex === -1 ? '' : href.slice(hashIndex);

    // Cross-page hash link — defer scroll to <ScrollToHash />.
    if (path && path !== window.location.pathname) {
      navigate(path + hash);
      return;
    }

    if (hashIndex === -1) {
      // Plain SPA route (no hash).
      if (href.startsWith('/')) navigate(href);
      return;
    }

    const targetId = href.substring(hashIndex + 1);
    if (!document.getElementById(targetId)) return;

    const trackedSectionId = DESKTOP_HREF_TO_SECTION_MAP[`#${targetId}`];

    // Pause section tracking during programmatic scroll
    setIsNavigating(true);
    if (trackedSectionId) setActiveSection(trackedSectionId);

    scrollToElement(targetId);

    setTimeout(() => setIsNavigating(false), 1000);
  };

  return (
    <motion.li
      ref={ref}
      onMouseEnter={() => {
        if (!ref?.current) return;

        const { width } = ref.current.getBoundingClientRect();

        setPosition({
          left: ref.current.offsetLeft,
          width,
          opacity: 1,
        });
      }}
      className="relative z-10 rounded-full"
      animate={{
        backgroundColor: isActive ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0)"
      }}
      transition={{
        type: "spring",
        stiffness: 350,
        damping: 25,
      }}
    >
      <a
        href={href}
        onClick={handleClick}
        className="relative z-10 block cursor-pointer px-4 py-2 text-sm font-medium text-foreground mix-blend-difference whitespace-nowrap"
      >
        <span className="flex items-center gap-2">
          {children}
        </span>
      </a>
    </motion.li>
  );
};